import { defineStore } from "pinia";
import { markRaw } from "vue";
import posthog from "posthog-js";
import axios from "axios";
import { sdk } from "@/js/sdk";
import { SdkError, ERROR_CODES } from "@signaloid/cloud-compute-engine-client";
import { monitoringCaptureError } from "@/plugins/monitoring";
import * as dsUtil from "@/components/DataSources/utilities";

export type UploadItemStatus = "queued" | "uploading" | "success" | "failed";

export interface UploadItem {
	id: number;
	file: File;
	fileName: string;
	destination: string;
	path: string;
	status: UploadItemStatus;
	error?: string;
}

interface UploadsState {
	items: UploadItem[];
	nextId: number;
	isRunning: boolean;
	trayOpen: boolean;
	// Increments whenever a run drains with at least one success, so views can refresh.
	completionTick: number;
	// Lets a confirmed sign-out skip UploadTray's beforeunload prompt, avoiding a double-prompt.
	bypassUnloadGuard: boolean;
}

function resolveUploadError(error: unknown): string {
	if (error instanceof SdkError) {
		// The backend's message names which limit was reached, so prefer it.
		const fallback =
			error.code === ERROR_CODES.API_FORBIDDEN
				? "Could not upload file due to tier restrictions."
				: "Upload failed. Please try again.";
		return error.message || fallback;
	}
	if (axios.isAxiosError(error)) {
		// The SDK's upload() does a direct axios PUT to the S3 presigned URL;
		// a raw axios error here means that second hop failed (S3 rejection or network).
		return error.response
			? "Upload failed. Please try again."
			: "Failed to upload the file. Make sure you have an active internet connection.";
	}
	if (error instanceof Error) {
		return error.message;
	}
	return "Unknown error occurred. Please try again.";
}

/**
 * Owns Signaloid Cloud Storage uploads independently of any dialog, so an upload
 * keeps running (and its result still lands) after the picker dialog closes or the
 * user navigates away. Runs the queue sequentially and exposes per-file state to the
 * global UploadTray.
 */
export const useUploadsStore = defineStore("uploads", {
	state: (): UploadsState => ({
		items: [],
		nextId: 1,
		isRunning: false,
		trayOpen: false,
		completionTick: 0,
		bypassUnloadGuard: false,
	}),
	getters: {
		hasActive: (state): boolean =>
			state.items.some((item) => item.status === "queued" || item.status === "uploading"),
		totalCount: (state): number => state.items.length,
		successCount: (state): number => state.items.filter((item) => item.status === "success").length,
		failedItems: (state): UploadItem[] => state.items.filter((item) => item.status === "failed"),
		settledCount: (state): number =>
			state.items.filter((item) => item.status === "success" || item.status === "failed").length,
	},
	actions: {
		enqueue(files: File[], destination: string | undefined) {
			const dest = destination ?? "";
			for (const file of files) {
				const path = dsUtil.sanitizeStoragePath(dest ? `${dest}/${file.name}` : file.name);
				this.items.push({
					id: this.nextId++,
					file: markRaw(file),
					fileName: file.name,
					destination: dest,
					path,
					status: "queued",
				});
			}
			this.trayOpen = true;
			this.run();
		},
		// Safe to call repeatedly: the isRunning guard keeps a single sequential loop.
		async run() {
			if (this.isRunning) {
				return;
			}
			this.isRunning = true;
			let succeeded = 0;
			try {
				let next: UploadItem | undefined;
				while ((next = this.items.find((item) => item.status === "queued"))) {
					next.status = "uploading";
					try {
						await sdk.files.upload(next.path, next.file);
						next.status = "success";
						succeeded++;
					} catch (error) {
						next.status = "failed";
						next.error = resolveUploadError(error);
						// 403 = tier/quota limit: expected, not a bug — keep it out of Sentry (matches
						// the tasks store). The type guard is load-bearing too: monitoringCaptureError
						// throws on a non-Error value, which would otherwise escape and halt the queue.
						const isTierLimit = error instanceof SdkError && error.code === ERROR_CODES.API_FORBIDDEN;
						const isReportable = axios.isAxiosError(error) || error instanceof Error;
						if (isReportable && !isTierLimit) {
							monitoringCaptureError(error, "Upload files to user cloud storage");
						}
					}
				}
			} finally {
				this.isRunning = false;
				if (succeeded > 0) {
					posthog?.capture("cloud_storage_file_uploaded", { file_count: succeeded });
					this.completionTick++;
				}
			}
		},
		retry(id: number) {
			const item = this.items.find((entry) => entry.id === id);
			if (item && item.status === "failed") {
				item.status = "queued";
				item.error = undefined;
				this.run();
			}
		},
		retryAllFailed() {
			let hasRequeued = false;
			for (const item of this.items) {
				if (item.status === "failed") {
					item.status = "queued";
					item.error = undefined;
					hasRequeued = true;
				}
			}
			if (hasRequeued) {
				this.run();
			}
		},
		removeItem(id: number) {
			this.items = this.items.filter((item) => item.id !== id);
			if (this.items.length === 0) {
				this.trayOpen = false;
			}
		},
		clearFinished() {
			this.items = this.items.filter((item) => item.status === "queued" || item.status === "uploading");
			if (this.items.length === 0) {
				this.trayOpen = false;
			}
		},
	},
});
